// Promise-chain queue. Each item has a separate preload phase (called eagerly for the
// next `preloadCount` items) and a play phase (called serially when it's the item's turn).
// Supports pause/resume (blocks next item from starting, delegates to the current item's
// pause/resume callbacks) and skip (aborts the current item).
export default class AlertQueue {
	#chain = Promise.resolve();
	#pending = []; // { data, preload, play, pause, resume, loadPromise, state }
	#currentData = null;
	#currentEntry = null;
	#currentAbort = null;
	#pausePromise = null;
	#pauseResolve = null;

	timeoutMs = 10000;
	preloadCount = 3;
	onSizeChange = null;

	get size() { return this.#pending.length + (this.#currentData !== null ? 1 : 0); }
	get pendingItems() { return this.#pending.map(e => e.data); }
	get currentItem() { return this.#currentData; }

	append({ data, preload, play, pause, resume }) {
		this.#pending.push({ data, preload, play, pause, resume, loadPromise: null, state: 'queued' });
		this._triggerPreload();
		this.onSizeChange?.(this.size);
		this.onChange?.();
		this.#chain = this.#chain.then(() => this._run());
	}

	_preloadEntry(entry) {
		return entry.loadPromise ||= new Promise((resolve, reject) => {
			entry.state = 'loading';
			this.onChange?.();
			const abort = new AbortController();
			const id = setTimeout(() => abort.abort(), this.timeoutMs);
			return entry.preload?.(abort.signal).then(resolve).catch(reject).finally(() => {
				entry.state = 'finished';
				this.onChange?.();
				clearTimeout(id);
			});
		});
	}

	_triggerPreload() {
		const count = Math.min(this.preloadCount, this.#pending.length);
		for(let i = 0; i < count; i++) {
			const entry = this.#pending[i];
			this._preloadEntry(entry).catch(e => console.error('Could not preload entry', entry, e));
		}
	}

	async _run() {
		if(this.#pausePromise) await this.#pausePromise;
		const entry = this.#pending.shift();
		if(!entry) return; // slot left over from a cancel

		try {
			await this._preloadEntry(entry);
			this.#currentData = entry.data;
			this.#currentEntry = entry;
			this._triggerPreload(); // preload window moved forward, load next item if needed
			this.onSizeChange?.(this.size);
			this.onChange?.();

			const abort = new AbortController();
			this.#currentAbort = abort;
			try {
				await entry.play(abort.signal);
			} catch(e) {
				if(!abort.signal.aborted) console.error('AlertQueue item error', e);
			} finally {
				if(this.#currentAbort === abort) this.#currentAbort = null;
				this.#currentData = null;
				this.#currentEntry = null;
				this.onSizeChange?.(this.size);
				this.onChange?.();
			}
		} catch(e) {
			console.error('Could not _run', entry, e);
		}
	}

	cancel(predicate) {
		const removed = this.#pending.filter(e => predicate(e.data));
		this.#pending = this.#pending.filter(e => !predicate(e.data));
		if(this.#currentData && predicate(this.#currentData)) this.skip();
		if(removed.length) {
			this.onSizeChange?.(this.size);
			this.onChange?.();
		}
		return removed.map(e => e.data);
	}

	skip() { this.#currentAbort?.abort(); }

	pause() {
		if(this.#pausePromise) return;
		this.#pausePromise = new Promise(resolve => { this.#pauseResolve = resolve; });
		this.#currentEntry?.pause?.();
	}

	resume() {
		if(!this.#pausePromise) return;
		this.#pauseResolve?.();
		this.#pausePromise = null;
		this.#pauseResolve = null;
		this.#currentEntry?.resume?.();
	}
}
